import { View, Text, Pressable, ScrollView } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { useState, useMemo, useCallback } from 'react';
import { useRouter } from 'expo-router';
import { DateTime } from 'luxon';
import { Query } from '@/utils/react-query/Queries';
import { dayContainer } from '@/utils/reportDates';
import { Table } from '@/components/General/Table';
import SolidButton from '@/components/General/SolidButton';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import Checkbox from '@/components/General/inputs/Checkbox';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import DataExporter from '@/utils/DataExporter';
import ContributionStatementPDF from '@/components/Contributions/ContributionStatementPDF';
import Unicon from '@/components/General/Unicon';
import SheetModal from '@/components/General/SheetModal';
import GivingReceipt from '@/components/Giving/GivingReceipt';
import ManagePaymentMethodsModal from '@/components/Giving/ManagePaymentMethodsModal';
import ManageRecurringGiftsModal from '@/components/Giving/ManageRecurringGiftsModal';
import { isReportableContribution, contributionStatusLabel } from '@/utils/contributionStatus';

const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);

// Year-to-date defaults
const ytdStart = dayContainer(DateTime.now().startOf('year'));
const ytdEnd = dayContainer(DateTime.now());

const DATE_PRESETS = [
    { label: 'Year to Date', start: (tz) => dayContainer(DateTime.now().setZone(tz).startOf('year')), end: (tz) => dayContainer(DateTime.now().setZone(tz)) },
    { label: 'Last Year', start: (tz) => dayContainer(DateTime.now().setZone(tz).minus({ years: 1 }).startOf('year')), end: (tz) => dayContainer(DateTime.now().setZone(tz).minus({ years: 1 }).endOf('year')) },
    { label: 'Last 90 Days', start: (tz) => dayContainer(DateTime.now().setZone(tz).minus({ days: 90 })), end: (tz) => dayContainer(DateTime.now().setZone(tz)) },
    { label: 'All Time', start: () => new Date('2000-01-01'), end: (tz) => dayContainer(DateTime.now().setZone(tz)) },
];

/**
 * GivingTab
 *
 * Props:
 *   personID   – the person whose transactions to show
 *   person     – person profile object (for statement PDF header)
 *   family     – family member person objects (for "Include Family")
 *   canViewContributions – viewer has 'View Contributions' permission or is admin
 */
const GivingTab = ({ personID, person, family = [], canViewContributions = false }) => {
    const { theme: { colors, fonts } } = useUnistyles();
    const router = useRouter();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const canCreateContributions = loggedInUser?.can?.('Create Contributions');

    const [showFilters, setShowFilters] = useState(false);
    const [showSummaryDetails, setShowSummaryDetails] = useState(false);
    const { data: organization } = Query.organization.byId({});
    const orgTimezone = organization?.timezone || 'UTC';
    const [startDate, setStartDate] = useState(ytdStart);
    const [endDate, setEndDate] = useState(ytdEnd);
    const [selectedFund, setSelectedFund] = useState(null);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [activePreset, setActivePreset] = useState('YTD');
    const [showStatement, setShowStatement] = useState(false);
    const [receiptTransaction, setReceiptTransaction] = useState(null);
    const [includeFamily, setIncludeFamily] = useState(false);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [showManageMethods, setShowManageMethods] = useState(false);
    const [showRecurringGifts, setShowRecurringGifts] = useState(false);

    const organizationID = organization?.id || loggedInUser?.organizationID;
    const { data: paymentMethods } = Query.giving.paymentMethods({ organizationID, personID });

    const showFamilyControls = canViewContributions && family.length > 0;

    const formattedStart = DateTime.fromJSDate(startDate).toISODate();
    const formattedEnd = DateTime.fromJSDate(endDate).toISODate();

    const { data: transactions = [], isLoading } = Query.contributionTransactions.byPersonId({
        personID,
        personIDs: includeFamily ? [personID, ...family.map(f => f.id)] : undefined,
        startDate: formattedStart,
        endDate: formattedEnd,
        fundID: selectedFund?.id,
        methodID: selectedMethod?.id,
        options: { enabled: !!personID }
    });

    // Fake infinite-query shape so the Person SheetSelector only offers the
    // profile person + family members (type="people" reads query.data.pages).
    const personQueryResult = useMemo(() => ({
        data: { pages: [{ data: [person, ...family].filter(Boolean) }] },
        isFetching: false,
    }), [person, family]);

    const filteredTransactions = useMemo(() => {
        let results = transactions.filter(t => t.status !== 'CANCELED' || t.personID === loggedInUser?.id);
        if (includeFamily && selectedPerson) {
            results = results.filter(t => t.personID === selectedPerson.id);
        }
        if (!searchText) return results;
        const s = searchText.toLowerCase();
        return results.filter(t =>
            t.Method?.name?.toLowerCase().includes(s) ||
            t.Funds?.some(f => f.Fund?.name?.toLowerCase().includes(s)) ||
            t.methodDetailValue?.toLowerCase().includes(s) ||
            t.note?.toLowerCase().includes(s) ||
            String(t.amount).includes(s)
        );
    }, [transactions, searchText, includeFamily, selectedPerson, loggedInUser?.id]);

    const reportableTransactions = useMemo(() => filteredTransactions.filter(isReportableContribution), [filteredTransactions]);
    const totalGiving = useMemo(() =>
        reportableTransactions.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0),
        [reportableTransactions]
    );

    // Per-fund and per-method totals for the currently filtered transactions.
    // A transaction can split across funds, so fund totals sum each Funds[]
    // entry; method totals sum the whole transaction amount.
    const { fundTotals, methodTotals } = useMemo(() => {
        const fundMap = new Map();
        const methodMap = new Map();
        reportableTransactions.forEach(t => {
            t.Funds?.forEach(f => {
                const name = f.Fund?.name;
                if (!name) return;
                fundMap.set(name, (fundMap.get(name) || 0) + parseFloat(f.amount || 0));
            });
            const methodName = t.Method?.name;
            if (methodName) {
                methodMap.set(methodName, (methodMap.get(methodName) || 0) + parseFloat(t.amount || 0));
            }
        });
        const toSortedList = (map) => [...map.entries()]
            .map(([name, total]) => ({ name, total }))
            .sort((a, b) => b.total - a.total);
        return { fundTotals: toSortedList(fundMap), methodTotals: toSortedList(methodMap) };
    }, [reportableTransactions]);

    const rangeLabel = `${DateTime.fromJSDate(startDate).toLocaleString(DateTime.DATE_MED)} – ${DateTime.fromJSDate(endDate).toLocaleString(DateTime.DATE_MED)}`;

    const handlePreset = useCallback((preset) => {
        setActivePreset(preset.label);
        setStartDate(preset.start(orgTimezone));
        setEndDate(preset.end(orgTimezone));
    }, [orgTimezone]);

    const handleExportCSV = useCallback(() => {
        const rows = reportableTransactions.map(t => ({
            date: DateTime.fromISO(t.transactionDate).toLocaleString(DateTime.DATE_MED),
            name: `${t.Person?.firstName || ''} ${t.Person?.lastName || ''}`.trim(),
            method: t.Method?.name || '',
            methodDetail: t.methodDetailValue || '',
            funds: t.Funds?.map(f => `${f.Fund?.name}:${f.amount}`).join('; ') || '',
            amount: t.amount,
        }));
        DataExporter.exportToCSV(rows, `giving_${person?.firstName || ''}_${person?.lastName || ''}`);
    }, [reportableTransactions, person]);

    const columns = useMemo(() => [
        {
            value: 'Date',
            renderer: (item) => (
                <Text style={{ fontFamily: fonts.body, fontSize: 12, color: colors.textSecondary }}>
                    {DateTime.fromISO(item.transactionDate).toLocaleString(DateTime.DATE_MED)}
                </Text>
            )
        },
        ...(includeFamily ? [{
            value: 'Person',
            renderer: (item) => (
                <Text style={{ fontFamily: fonts.body, fontSize: 12, color: colors.text }}>
                    {`${item.Person?.firstName || ''} ${item.Person?.lastName || ''}`.trim() || '-'}
                </Text>
            )
        }] : []),
        {
            value: 'Method',
            renderer: (item) => (
                <View>
                    <Text style={{ fontFamily: fonts.bold, color: colors.text }}>{item.Method?.name || '-'}</Text>
                    {item.methodDetailValue && (
                        <Text style={{ color: colors.textSecondary, fontSize: 12 }}>
                            {item.Method?.detailName} {item.methodDetailValue}
                        </Text>
                    )}
                </View>
            )
        },
        {
            value: 'Funds',
            renderer: (item) => (
                <View>
                    {item.Funds?.map(f => (
                        <View key={f.Fund?.id} style={styles.fundTag}>
                            <Text style={{ color: colors.primary, fontSize: 12 }}>{f.Fund?.name}</Text>
                            <Text style={{ color: colors.textSecondary, fontSize: 11 }}>{formatCurrency(f.amount)}</Text>
                        </View>
                    ))}
                </View>
            )
        },
        {
            value: 'Status',
            renderer: (item) => (
                <View testID={`giving-payment-status-${item.id}`}>
                    <Text style={{ fontFamily: fonts.bold, color: isReportableContribution(item) ? colors.textSecondary : colors.warning }}>
                        {contributionStatusLabel(item)}
                    </Text>
                    {item.returnReason && (
                        <Text style={{ fontFamily: fonts.body, fontSize: 12, color: colors.textSecondary }}>{item.returnReason}</Text>
                    )}
                </View>
            )
        },
        {
            value: 'Amount',
            renderer: (item) => (
                <Text style={{ fontFamily: fonts.bold, color: isReportableContribution(item) ? colors.success : colors.textSecondary, fontSize: 14 }}>
                    {formatCurrency(item.amount)}
                </Text>
            )
        },
    ], [colors, fonts, includeFamily]);

    const tableActions = useMemo(() => [
        {
            name: 'Receipt',
            icon: { web: 'receipt-outline' },
            isAvailable: (item) => item.Payin?.length > 0 && isReportableContribution(item),
            onSelect: (item) => setReceiptTransaction(item),
        },
        // ReachGiving transactions carry a GivingPayin row and are not
        // editable through the transaction form (same gating as the
        // 'Receipt' action above and the contributions list view).
        ...(canCreateContributions ? [{
            name: 'Edit',
            icon: dropdownIcons.edit,
            isAvailable: (item) => !(item.Payin?.length > 0),
            onSelect: (item) => router.push({ pathname: "/contributions/transactions/create", params: { id: item.id, batchID: item.batchID } }),
        }] : []),
    ], [canCreateContributions, router]);

    // Build GivingReceipt receiptData from the selected transaction + its
    // GivingPayins embed. Payin.amount is the gross charged amount in cents;
    // tx.amount is the gift amount in dollars.
    const receiptData = useMemo(() => {
        const payin = receiptTransaction?.Payin?.[0];
        if (!receiptTransaction || !payin) return null;
        const raw = payin.rawPayload || {};
        const amountCents = Math.round((receiptTransaction.amount || 0) * 100);
        const grossAmountCents = payin.amount != null && Number.isFinite(Number(payin.amount))
            ? Math.round(Number(payin.amount))
            : null;
        return {
            payinID: payin.payinID || 'N/A',
            amountCents,
            grossAmountCents,
            convenienceFeeCents: grossAmountCents != null ? grossAmountCents - amountCents : undefined,
            methodType: payin.methodType,
            fundSplits: (receiptTransaction.Funds || []).map(f => ({
                fundID: f.Fund?.id,
                fundName: f.Fund?.name,
                amountCents: Math.round((f.amount || 0) * 100),
            })),
            note: receiptTransaction.note,
            billingContact: raw.billing_contact || {},
            card: raw.card || raw.payment_method?.card || null,
            ach: raw.ach || raw.payment_method?.ach || null,
            date: receiptTransaction.transactionDate,
        };
    }, [receiptTransaction]);

    return (
        <View style={styles.container}>
            {/* Preset Chips */}
            <View>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.presetsScroll} contentContainerStyle={styles.presetsRow} keyboardShouldPersistTaps="handled"
>
                    {DATE_PRESETS.map(preset => (
                        <Pressable
                            key={preset.label}
                            onPress={() => handlePreset(preset)}
                            style={[styles.presetChip, activePreset === preset.label && styles.presetChipActive]}
                        >
                            <Text style={[styles.presetChipText, activePreset === preset.label && styles.presetChipTextActive]}>
                                {preset.label}
                            </Text>
                        </Pressable>
                    ))}
                </ScrollView>
            </View>

            {/* Total giving summary — always visible, even when filters collapse */}
            <View style={styles.summaryRow}>
                <View style={styles.summaryCard}>
                    <Pressable
                        testID="giving-summary-toggle"
                        accessibilityRole="button"
                        accessibilityLabel={showSummaryDetails ? 'Hide giving breakdown' : 'Show giving breakdown'}
                        accessibilityState={{ expanded: showSummaryDetails }}
                        onPress={() => setShowSummaryDetails(value => !value)}
                        style={styles.summaryHeader}
                    >
                        <View>
                            <Text style={styles.summaryLabel}>Total Giving</Text>
                            <View style={styles.summaryValueRow}>
                                <Text selectable style={styles.summaryValue}>{isLoading ? '—' : formatCurrency(totalGiving)}</Text>
                                <Unicon name={showSummaryDetails ? 'chevron-up' : 'chevron-down'} size={16} colorVariant="textSecondary" />
                            </View>
                        </View>
                        <View style={styles.summaryMeta}>
                            <Text style={styles.summaryCount}>
                                {isLoading ? 'Loading…' : `${reportableTransactions.length} contribution${reportableTransactions.length !== 1 ? 's' : ''}`}
                            </Text>
                            <Text style={styles.summarySubtext}>{rangeLabel}</Text>
                        </View>
                    </Pressable>
                    {showSummaryDetails && (
                        <View style={styles.summaryDetails}>
                            {!isLoading && (fundTotals.length > 0 || methodTotals.length > 0) && (
                                <View style={styles.breakdownsRow}>
                                    {fundTotals.length > 0 && (
                                        <View style={styles.breakdownGroup}>
                                            <Text style={styles.breakdownTitle}>By Fund</Text>
                                            <View style={styles.breakdownListContent}>
                                                {fundTotals.map(f => (
                                                    <View key={f.name} style={styles.breakdownItem}>
                                                        <Text style={styles.breakdownName} numberOfLines={1}>{f.name}</Text>
                                                        <Text selectable style={styles.breakdownAmount}>{formatCurrency(f.total)}</Text>
                                                    </View>
                                                ))}
                                            </View>
                                        </View>
                                    )}
                                    {methodTotals.length > 0 && (
                                        <View style={styles.breakdownGroup}>
                                            <Text style={styles.breakdownTitle}>By Method</Text>
                                            <View style={styles.breakdownListContent}>
                                                {methodTotals.map(m => (
                                                    <View key={m.name} style={styles.breakdownItem}>
                                                        <Text style={styles.breakdownName} numberOfLines={1}>{m.name}</Text>
                                                        <Text selectable style={styles.breakdownAmount}>{formatCurrency(m.total)}</Text>
                                                    </View>
                                                ))}
                                            </View>
                                        </View>
                                    )}
                                </View>
                            )}
                        </View>
                    )}
                </View>
            </View>

            {/* Filters toggle + giving management actions */}
            <View style={styles.filtersBar}>
                <Pressable
                    testID="giving-filters-toggle"
                    onPress={() => setShowFilters(v => !v)}
                    style={styles.filtersToggle}
                >
                    <Unicon name={showFilters ? 'options' : 'options-outline'} size={16} colorVariant={'primary'} />
                    <Text style={[styles.filtersToggleText, showFilters && styles.filtersToggleTextActive]}>
                        {showFilters ? 'Hide Filters' : 'Show Filters'}
                        {(selectedFund || selectedMethod || selectedPerson) ? ` (${[selectedFund, selectedMethod, selectedPerson].filter(Boolean).length})` : ''}
                    </Text>
                </Pressable>
                <View style={styles.manageActions}>
                    <Pressable
                        testID="giving-manage-recurring"
                        onPress={() => setShowRecurringGifts(true)}
                        style={styles.manageAction}
                    >
                        <Text style={styles.manageActionText}>Manage Recurring Giving</Text>
                    </Pressable>
                    <Pressable
                        testID="giving-manage-methods"
                        onPress={() => setShowManageMethods(true)}
                        style={styles.manageAction}
                    >
                        <Text style={styles.manageActionText}>Manage Payment Methods</Text>
                    </Pressable>
                </View>
            </View>

            {/* Filters + breakdowns */}
            {showFilters && <View>
                <View style={styles.filtersRow}>
                    <View style={styles.filterItem}>
                        <DatePickerInput title="Start Date" value={startDate} onUpdate={(d) => { setStartDate(d); setActivePreset(null); }} hideClearIcon />
                    </View>
                    <View style={styles.filterItem}>
                        <DatePickerInput title="End Date" value={endDate} onUpdate={(d) => { setEndDate(d); setActivePreset(null); }} hideClearIcon />
                    </View>
                    <View style={styles.filterItem}>
                        <SheetSelector
                            title="Fund"
                            type="contributionFunds"
                            placeholder="All Funds"
                            value={selectedFund}
                            onChange={setSelectedFund}
                            titleOnly
                        />
                    </View>
                    <View style={styles.filterItem}>
                        <SheetSelector
                            title="Method"
                            type="contributionMethods"
                            placeholder="All Methods"
                            value={selectedMethod}
                            onChange={setSelectedMethod}
                            titleOnly
                        />
                    </View>
                    {includeFamily && (
                        <View style={styles.filterItem}>
                            <SheetSelector
                                title="Person"
                                type="people"
                                placeholder="All People"
                                value={selectedPerson}
                                onChange={setSelectedPerson}
                                queryResult={personQueryResult}
                                titleOnly
                            />
                        </View>
                    )}
                    {showFamilyControls && (
                        <View style={[styles.filterItem, styles.checkboxFilterItem]}>
                            <Checkbox
                                testID="giving-include-family"
                                label="Include Family"
                                checked={includeFamily}
                                onChange={(checked) => {
                                    setIncludeFamily(checked);
                                    if (!checked) setSelectedPerson(null);
                                }}
                            />
                        </View>
                    )}
                </View>

            </View>}

            {/* Table */}
            <View style={styles.tableContainer}>
                <Table
                    title="Giving History"
                    description={isLoading ? '' : `${filteredTransactions.length} transaction${filteredTransactions.length !== 1 ? 's' : ''}`}
                    columns={columns}
                    actions={tableActions}
                    data={filteredTransactions}
                    isLoading={isLoading}
                    onSearchChange={setSearchText}
                    estimatedItemSize={70}
                    ListEmptyComponent={!isLoading && <EmptyListPlaceholder message="No giving records found" />}
                    headerRightContent={
                        <DropdownMenu
                            title="Export"
                            triggerComponent={
                                <SolidButton
                                    size="sm"
                                    disabled={reportableTransactions.length === 0}
                                    iconName="chevron-down"
                                    text="Export"
                                    variant="muted"
                                    type="primary"
                                />
                            }
                            options={[
                                { name: 'Export CSV', icon: { web: 'download' }, onSelect: handleExportCSV },
                                { name: 'Export Statement', icon: { web: 'document-text' }, onSelect: () => setShowStatement(true) },
                            ]}
                        />
                    }
                />
            </View>

            {/* Statement PDF (invisible trigger) */}
            {showStatement && (
                <ContributionStatementPDF
                    person={person}
                    transactions={reportableTransactions}
                    startDate={formattedStart}
                    endDate={formattedEnd}
                    onClose={() => setShowStatement(false)}
                />
            )}

            {/* Reach Giving receipt */}
            <SheetModal
                isOpen={!!receiptTransaction}
                title="Giving Receipt"
                onClose={() => setReceiptTransaction(null)}
                cancelText="Close"
            >
                {receiptData && (
                    <GivingReceipt
                        receiptData={receiptData}
                        personEmail={person?.email}
                    />
                )}
            </SheetModal>

            <ManageRecurringGiftsModal
                isOpen={showRecurringGifts}
                onClose={() => setShowRecurringGifts(false)}
                organizationID={organizationID}
                personID={personID}
            />
            <ManagePaymentMethodsModal
                isOpen={showManageMethods}
                onClose={() => setShowManageMethods(false)}
                methods={paymentMethods || []}
                organizationID={organizationID}
                personID={personID}
            />
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows, roundness }, { isPortrait }) => ({
    container: {
        flex: 1,
    },
    presetsScroll: {
        flexShrink: 0,
        paddingTop: 10,
    },
    presetsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 12,
        paddingBottom: 8,
    },
    presetChip: {
        paddingHorizontal: 14,
        paddingVertical: 6,
        borderRadius: 16,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        alignSelf: 'center',
    },
    presetChipActive: {
        backgroundColor: colors.primary,
        borderColor: colors.primary,
    },
    presetChipText: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
    },
    presetChipTextActive: {
        color: colors.onPrimary || '#fff',
        fontFamily: fonts.bold,
    },
    filtersRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
        paddingHorizontal: 12,
        paddingBottom: 10,
    },
    filterItem: {
        flexGrow: isPortrait ? 1 : 0,
        minWidth: isPortrait ? '45%' : 180,
    },
    checkboxFilterItem: {
        justifyContent: 'flex-end',
        paddingBottom: 20,
    },
    summaryRow: {
        paddingHorizontal: 12,
        paddingBottom: 8,
    },
    summaryCard: {
        backgroundColor: colors.surface,
        borderRadius: roundness.lg,
        padding: 12,
        paddingLeft: 15,
        boxShadow: shadows.card,
        borderLeftWidth: 3,
        borderLeftColor: colors.giving,
    },
    summaryHeader: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 8,
    },
    summaryLabel: {
        fontSize: 10,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        marginBottom: 3,
        letterSpacing: 0.5,
    },
    summaryValue: {
        fontSize: 22,
        fontFamily: fonts.bold,
        fontVariant: ['tabular-nums'],
        color: colors.giving,
    },
    summaryValueRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    summaryMeta: {
        flexShrink: 1,
        alignItems: 'flex-end',
    },
    summaryCount: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    summaryDetails: {
        marginTop: 9,
        paddingTop: 8,
        borderTopWidth: 1,
        borderTopColor: colors.borderSubtle,
    },
    summarySubtext: {
        fontSize: 11,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'right',
    },
    breakdownsRow: {
        flexDirection: 'row',
        gap: isPortrait ? 12 : 24,
    },
    breakdownGroup: {
        flex: 1,
        minWidth: 0,
    },
    breakdownTitle: {
        fontSize: 10,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 4,
    },
    breakdownListContent: {
        gap: 3,
    },
    breakdownItem: {
        minHeight: 20,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 6,
    },
    breakdownName: {
        flex: 1,
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.text,
    },
    breakdownAmount: {
        flexShrink: 0,
        fontSize: 12,
        fontFamily: fonts.bold,
        fontVariant: ['tabular-nums'],
        color: colors.giving,
        textAlign: 'right',
    },
    filtersBar: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 8,
        marginHorizontal: 12,
        marginBottom: 8,
    },
    filtersToggle: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 14,
        paddingVertical: 6,
    },
    filtersToggleText: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.primary,
    },
    filtersToggleTextActive: {
        fontFamily: fonts.bold,
    },
    manageActions: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 14,
    },
    manageAction: {
        paddingVertical: 6,
        paddingHorizontal: 2,
    },
    manageActionText: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.primary,
    },
    tableContainer: {
        flex: 1,
        minHeight: 300,
    },
    fundTag: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingBottom: 2,
        paddingHorizontal: 8,
        borderRadius: 5,
        backgroundColor: colors.primaryContainer,
        margin: 2,
        alignSelf: 'flex-start',
    },
}));

export default GivingTab;
